import { useEffect, useRef, useState } from "react";
import { api, fmtNum, today, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, DollarSign, ArrowUpDown, Pencil, X } from "lucide-react";
import PreciosReporte from "@/pages/PreciosReporte";

const PRODUCTOS = ["Propano", "Butano"];
const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export default function Precios() {
  const { can } = useAuth();
  const canEdit = can("precios", "edit");
  const [tab, setTab] = useState("captura");
  const [rows, setRows] = useState([]);
  const [tc, setTc] = useState([]);
  const [form, setForm] = useState({
    fecha: today(), producto: "Propano", bajo: "", alto: "", p_diario: "", p_mensual: "", variacion: "",
  });
  const [tcForm, setTcForm] = useState({ fecha: today(), valor: "" });
  const [loadingTc, setLoadingTc] = useState(false);
  const [dofPreview, setDofPreview] = useState(null);
  const [dofLoading, setDofLoading] = useState(false);
  const now = new Date();
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [anio, setAnio] = useState(String(now.getFullYear()));
  const [sortAsc, setSortAsc] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const overlayDown = useRef(false);

  const mesParam = `${anio}-${String(mes).padStart(2, "0")}`;
  const load = () => {
    api.get(`/precios?mes=${mesParam}`).then((r) => setRows(r.data));
    api.get("/tipo-cambio").then((r) => setTc(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mes, anio]);

  // Vista previa del DOF según la fecha del panel de captura
  useEffect(() => {
    if (!form.fecha) { setDofPreview(null); return; }
    let cancel = false;
    setDofLoading(true);
    const t = setTimeout(() => {
      api.get(`/tipo-cambio/dof?fecha=${form.fecha}`)
        .then((r) => { if (!cancel) setDofPreview(r.data); })
        .catch(() => { if (!cancel) setDofPreview(null); })
        .finally(() => { if (!cancel) setDofLoading(false); });
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [form.fecha]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        fecha: form.fecha,
        producto: form.producto,
        bajo: parseFloat(form.bajo) || 0,
        alto: parseFloat(form.alto) || 0,
        p_diario: parseFloat(form.p_diario) || 0,
        p_mensual: parseFloat(form.p_mensual) || 0,
        variacion: parseFloat(form.variacion) || 0,
      };
      const res = await api.post("/precios", payload);
      const usado = res.data?.tipo_cambio;
      toast.success(usado != null ? `Precio registrado · DOF ${fmtNum(usado)}` : "Precio registrado");
      setForm({ ...form, bajo: "", alto: "", p_diario: "", p_mensual: "", variacion: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    await api.delete(`/precios/${id}`);
    toast.success("Eliminado");
    load();
  };

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      fecha: r.fecha, producto: r.producto,
      bajo: r.bajo ?? "", alto: r.alto ?? "", p_diario: r.p_diario ?? "",
      p_mensual: r.p_mensual ?? "", variacion: r.variacion ?? "",
      tipo_cambio: r.tipo_cambio ?? "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        fecha: editForm.fecha,
        producto: editForm.producto,
        bajo: parseFloat(editForm.bajo) || 0,
        alto: parseFloat(editForm.alto) || 0,
        p_diario: parseFloat(editForm.p_diario) || 0,
        p_mensual: parseFloat(editForm.p_mensual) || 0,
        variacion: parseFloat(editForm.variacion) || 0,
      };
      if (editForm.tipo_cambio !== "" && editForm.tipo_cambio != null) {
        payload.tipo_cambio = parseFloat(editForm.tipo_cambio);
      }
      await api.put(`/precios/${editing.id}`, payload);
      toast.success("Precio actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sortedRows = [...rows].sort((a, b) =>
    sortAsc ? a.fecha.localeCompare(b.fecha) : b.fecha.localeCompare(a.fecha)
  );

  const fetchAuto = async () => {
    setLoadingTc(true);
    try {
      const r = await api.post(`/tipo-cambio/auto?fecha=${form.fecha}`);
      toast.success(`DOF ${r.data.fecha}: ${fmtNum(r.data.valor)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingTc(false);
    }
  };

  const saveTc = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tipo-cambio", { fecha: form.fecha, valor: parseFloat(tcForm.valor) || 0, fuente: "Manual" });
      toast.success("Tipo de cambio guardado");
      setTcForm({ valor: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  const tabsBar = (
    <div className="inline-flex bg-slate-100 rounded-md p-1">
      <button data-testid="tab-precios-captura" onClick={() => setTab("captura")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "captura" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Captura
      </button>
      <button data-testid="tab-precios-reporte" onClick={() => setTab("reporte")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "reporte" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Reporte
      </button>
    </div>
  );

  if (tab === "reporte") {
    return (
      <div className="space-y-6">
        {tabsBar}
        <PreciosReporte />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {tabsBar}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Capture form */}
        {canEdit && (
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-900">Captura diaria de Precios Mont Belvieu</h3>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="text-xs text-slate-500">Fecha</label>
                <input data-testid="precio-fecha" type="date" className={input} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="text-xs text-slate-500">Producto</label>
                <select data-testid="precio-producto" className={input} value={form.producto} onChange={(e) => setForm({ ...form, producto: e.target.value })}>
                  {PRODUCTOS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              {[
                ["bajo", "Bajo"], ["alto", "Alto"], ["p_diario", "P. Diario"],
                ["p_mensual", "P. Mensual"], ["variacion", "Variación %"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="text-xs text-slate-500">{label}</label>
                  <input data-testid={`precio-${key}`} type="number" step="any" className={input} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder="0.00" />
                </div>
              ))}
              <div className="col-span-2 md:col-span-1 flex items-end">
                <button data-testid="precio-submit" className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                  <Plus className="h-4 w-4" /> Agregar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tipo de cambio */}
        <div className={`bg-white border border-slate-200 rounded-md shadow-sm ${canEdit ? "" : "lg:col-span-3"}`}>
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-600" />
            <h3 className="font-heading font-semibold text-slate-900">Tipo de Cambio DOF</h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Principal: DOF en tiempo real para la fecha de captura */}
            <div className="rounded-md bg-slate-900 text-white p-4 text-center" data-testid="dof-preview">
              <p className="text-xs uppercase tracking-[0.15em] text-slate-300">DOF para {form.fecha}</p>
              <p className="font-heading text-4xl font-bold mt-1 tabular-nums">
                {dofLoading ? "…" : (dofPreview && dofPreview.valor != null ? fmtNum(dofPreview.valor) : "—")}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {dofLoading
                  ? "Obteniendo DOF…"
                  : dofPreview && dofPreview.valor != null
                    ? `${dofPreview.fecha} · ${dofPreview.fuente}`
                    : "Sin DOF disponible para la fecha"}
              </p>
            </div>
            {/* Secundario: último registrado */}
            <div className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2" data-testid="tc-ultimo">
              <span className="text-slate-500">Último registrado</span>
              <span className="tabular-nums font-medium text-slate-900">{tc[0] ? `${fmtNum(tc[0].valor)} · ${tc[0].fecha}` : "—"}</span>
            </div>
            {canEdit && (
              <>
                <button data-testid="tc-auto" onClick={fetchAuto} disabled={loadingTc} className="w-full border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50 flex items-center justify-center gap-2 disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${loadingTc ? "animate-spin" : ""}`} /> Obtener DOF de la fecha
                </button>
                <form onSubmit={saveTc} className="space-y-2">
                  <p className="text-xs text-slate-500">O captura/modifica manualmente para el {form.fecha}:</p>
                  <input data-testid="tc-valor" type="number" step="any" className={input} placeholder="Ej. 17.85" value={tcForm.valor} onChange={(e) => setTcForm({ ...tcForm, valor: e.target.value })} required />
                  <button data-testid="tc-save" className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm hover:bg-slate-800">Guardar manual</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold text-slate-900">Histórico de Precios Mont Belvieu</h3>
          <div className="flex items-center gap-2">
            <select data-testid="hist-mes" value={mes} onChange={(e) => setMes(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
              {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
            </select>
            <input data-testid="hist-anio" type="number" value={anio} onChange={(e) => setAnio(e.target.value)} className="w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
              <tr>
                <th className="px-4 py-3 font-medium text-left">
                  <button data-testid="hist-sort-fecha" onClick={() => setSortAsc((s) => !s)} className="inline-flex items-center gap-1 hover:text-slate-900">
                    Fecha <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                {["Producto", "Bajo", "Alto", "P. Diario", "P. Mensual", "Variación %", "DOF"].map((h) => (
                  <th key={h} className={`px-4 py-3 font-medium ${h === "Producto" ? "text-left" : "text-right"}`}>{h}</th>
                ))}
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody data-testid="precios-table">
              {sortedRows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Sin registros para {MESES[Number(mes) - 1]} {anio}</td></tr>
              )}
              {sortedRows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-slate-900">{r.fecha}</td>
                  <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{r.producto}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(r.bajo)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(r.alto)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(r.p_diario)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(r.p_mensual)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${r.variacion < 0 ? "text-red-600" : r.variacion > 0 ? "text-emerald-600" : ""}`}>{fmtNum(r.variacion)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" title={r.tipo_cambio_fuente ? `${r.tipo_cambio_fuente}${r.tipo_cambio_fecha ? " · " + r.tipo_cambio_fecha : ""}` : ""}>
                    {r.tipo_cambio != null ? fmtNum(r.tipo_cambio) : "—"}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`precio-edit-${r.id}`} onClick={() => openEdit(r)} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`precio-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onMouseDown={(e) => { overlayDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayDown.current) setEditing(null); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg" data-testid="precio-edit-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Editar precio</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Fecha</label>
                <input data-testid="edit-fecha" type="date" className={input} value={editForm.fecha} onChange={(e) => setEditForm({ ...editForm, fecha: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs text-slate-500">Producto</label>
                <select data-testid="edit-producto" className={input} value={editForm.producto} onChange={(e) => setEditForm({ ...editForm, producto: e.target.value })}>
                  {PRODUCTOS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              {[["bajo", "Bajo"], ["alto", "Alto"], ["p_diario", "P. Diario"], ["p_mensual", "P. Mensual"], ["variacion", "Variación %"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs text-slate-500">{l}</label>
                  <input data-testid={`edit-${k}`} type="number" step="any" className={input} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3 text-emerald-600" /> DOF (Tipo de Cambio) — edición manual</label>
                <input data-testid="edit-tipo_cambio" type="number" step="any" className={input} value={editForm.tipo_cambio} onChange={(e) => setEditForm({ ...editForm, tipo_cambio: e.target.value })} placeholder="Ej. 17.85000" />
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                <button data-testid="edit-save" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
